/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import styled from '@emotion/styled';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { CustomInputText } from 'components/inputs/CustomInput';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  CommonBG,
  CommonBorder,
  CommonText,
  StatusErrorColor,
  StatusConfirmColor,
  FontColor,
} from 'ui/theme/Color';
import { Code } from '../../models/common/CommonCode';
import { CommonCode, CommonCodeHeader } from '../../models/admin/CommonCode';
import {
  getCommonCodeHeader,
  getCommonCodeNamesCondition,
  setCommonCodes,
} from '../../apis/admin/CommonCode';
import { useCommonModal } from '../../hooks/useCommonModal';
import { CrudCode } from '../../models/common/Edit';

/**
 * UT_capacity_calculator_v2_0.html의 PAGE3(관경기준) 중
 * 관경별 최대유량 기준표를 이식한 페이지.
 * 표의 열(에어/질소/PCW 등 UT 종류)과 행(관경 사이즈)을 모두 공통코드(PRDN_PROC_CD)에서 가져온다.
 * - 열: 그룹코드 헤더의 optValNm1..15 중 값이 채워진 것만큼 (그룹 옵션이 늘어나도 자동 대응)
 * - 행: 그룹코드 상세 목록(디테일 코드) 전체, cmnCdNm이 관경 사이즈 라벨이 된다
 * - 값: 각 상세 코드 행의 optValCtn1..15 (열과 같은 번호)이 그 열의 최대유량 값
 * 저장(브라우저) 값은 localStorage 키 'lg_ut_standards_v8'에 담아
 * 메인 UT 페이지의 관경 자동산출과 공유한다.
 */

const STORAGE_KEY = 'lg_ut_standards_v8';
const CMN_GR_CD = 'PIPE_DMTR_STND';
const MAX_OPTIONS = 15;

const ACCENTS = [
  StatusErrorColor.StatusError500,
  FontColor.Primary,
  StatusConfirmColor.StatusConfirm500,
  '#7B1FA2',
  '#E67E00',
];

interface OptionColumn {
  field: keyof Code; // optValCtn1, optValCtn2, ...
  label: string; // 헤더의 optValNm{n} 값
}

// row key(cmnCd) -> field(optValCtnN) -> 편집 중인 값
type ValueMatrix = Record<string, Record<string, string>>;

const loadSavedValues = (): ValueMatrix => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // 저장된 값이 손상된 경우 무시하고 공통코드 값을 그대로 사용
  }
  return {};
};

const UtMatrixPipeDiameterPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const [header, setHeader] = useState<CommonCodeHeader | null>(null);
  const [detailCodes, setDetailCodes] = useState<Code[]>([]);
  const [values, setValues] = useState<ValueMatrix>({});

  // 공통코드 조회 ('조회' 버튼으로도 재호출) : 헤더(열 이름) + 상세코드(행) 동시 조회
  const getCommonCodes = async () => {
    const [headerResult, detailResult] = await Promise.all([
      getCommonCodeHeader(CMN_GR_CD),
      getCommonCodeNamesCondition({ cmnGrCd: CMN_GR_CD }),
    ]);
    setHeader(headerResult);
    const rows = detailResult || [];
    setDetailCodes(rows);

    const saved = loadSavedValues();
    setValues((prev) => {
      const next: ValueMatrix = {};
      rows.forEach((row) => {
        const rowKey = row.cmnCd || '';
        const rowValues: Record<string, string> = {};
        for (let i = 1; i <= MAX_OPTIONS; i++) {
          const field = `optValCtn${i}`;
          if (headerResult?.[`optValNm${i}` as keyof CommonCodeHeader]) {
            // 우선순위: 이전 편집값 > 저장된 값 > 공통코드 원본값
            const rowRecord = row as unknown as Record<string, string | undefined>;
            rowValues[field] =
              prev[rowKey]?.[field] ?? saved[rowKey]?.[field] ?? rowRecord[field] ?? '';
          }
        }
        next[rowKey] = rowValues;
      });
      return next;
    });
  };

  useEffect(() => {
    getCommonCodes();
  }, []);

  // 헤더에 이름(optValNm{n})이 채워진 옵션만큼만 열로 노출한다 (그룹코드 옵션 증감에 자동 대응)
  const columns: OptionColumn[] = [];
  for (let i = 1; i <= MAX_OPTIONS; i++) {
    const name = header?.[`optValNm${i}` as keyof CommonCodeHeader];
    if (name) {
      columns.push({ field: `optValCtn${i}` as keyof Code, label: name });
    }
  }

  const updateValue = (rowKey: string, field: string, rawValue: string) => {
    setValues((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], [field]: rawValue },
    }));
  };

  // 이 화면은 항상 기존 상세코드의 옵션값만 수정한다 (행 추가/삭제 없음) → crudKey는 항상 UPDATE.
  // 필수값: 그룹코드(cmnGrCd), 디테일 코드(cmnCd), 옵션 번호(1/2/3...), 그 옵션의 값.
  const handleSave = () => {
    const valid = detailCodes
      .map((row, index) => {
        if (!row.copCd || !row.cmnCd)
          return `${index + 1} : ${t(
            'admin.msg.공통코드명을 입력해 주세요.',
            '공통코드명을 입력해 주세요.'
          )}`;
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return;
    }

    const saveRows: CommonCode[] = detailCodes.map((row) => ({
      ...row,
      ...values[row.cmnCd || ''],
      crudKey: CrudCode.UPDATE,
    }));

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        setCommonCodes(saveRows).then((result) => {
          if (!result) {
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            return;
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          getCommonCodes();
        });
      },
    });
  };

  return (
    <ContainerLayout>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.관경기준', '관경기준')}</h3>
          <span className="total">
            {t(
              'ut.label.관경별 최대유량 기준값을 입력합니다',
              '관경별 최대유량 기준값을 입력합니다'
            )}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="find" onClick={getCommonCodes} disableRipple>
            {t('com.button.조회', '조회')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>

      <GridWrap>
        {columns.map((column, colIdx) => (
          <div key={column.field}>
            <TableCard accent={ACCENTS[colIdx % ACCENTS.length]}>
              <h3>{t('com.code.PIPE_DMTR_STND_1', '_' + column.label)}</h3>
              <DataTable>
                <thead>
                  <tr>
                    <th>{t('ut.label.관경', '관경')}</th>
                    <th>{t('ut.label.최대유량', '최대유량')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detailCodes.map((row) => {
                    const rowKey = row.cmnCd || '';
                    return (
                      <tr key={rowKey}>
                        <td className="size">{t(row.cmnCdNm || rowKey, row.cmnCdNm || rowKey)}</td>
                        <td>
                          <CustomInputText
                            type="number"
                            className="right"
                            value={values[rowKey]?.[column.field] ?? ''}
                            onChange={(e) => updateValue(rowKey, column.field, e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
            </TableCard>
          </div>
        ))}
      </GridWrap>

      <GlobalBtnGroup>
        <Button css={IconButton.button} className="save" onClick={handleSave}>
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>
    </ContainerLayout>
  );
};

export default UtMatrixPipeDiameterPage;

const GridWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const TableCard = styled.div<{ accent: string }>`
  background: ${CommonBG.Basic};
  border: 1px solid ${CommonBorder.Basic};
  padding: 18px 20px;
  border-radius: 8px;

  h3 {
    margin: 0 0 14px 0;
    font-size: 13px;
    font-weight: 700;
    color: ${CommonText.Basic};
    padding-bottom: 10px;
    border-bottom: 2px solid ${(props) => props.accent};
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  text-align: center;

  th {
    background: ${CommonBG.Deep};
    border: 1px solid ${CommonBorder.Basic};
    padding: 9px 10px;
    color: ${CommonText.Light};
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }

  td {
    border: 1px solid ${CommonBorder.Basic};
    padding: 7px 10px;
    vertical-align: middle;
  }

  td.size {
    font-weight: 700;
    background: ${CommonBG.Deep};
  }

  input {
    width: 100%;
  }
`;
